import { useCallback } from "react";
import { useFormHandler } from "shared/hooks";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import {
  ListByIdDocument,
  ListByIdQuery,
  useListUpdateByIdMutation,
} from "gql/generated";
import { createMultilingual, getMultilingualString } from "shared/utils";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.CustomLists]);

export const listDetailsSchema = z.object({
  name: z
    .string({
      required_error: `${i18n.t("customLists:LIST_DETAILS.FORM.LABELS.NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(1, {
      message: `${i18n.t("customLists:LIST_DETAILS.FORM.LABELS.NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    }),
  description: z.string().trim().optional().or(z.literal("")),
});

export type ListDetailsFormValues = z.infer<typeof listDetailsSchema>;

type UseListDetailsFormProps = {
  list: ListByIdQuery["listById"];
  onSuccess: () => void;
  onError: () => void;
};

export const useListDetailsForm = ({
  list,
  onSuccess,
  onError,
}: UseListDetailsFormProps) => {
  const defaultValues: ListDetailsFormValues = {
    name: getMultilingualString(list?.name ?? {}),
    description: getMultilingualString(list?.description ?? {}),
  };

  const [listUpdateByIdMutation] = useListUpdateByIdMutation({
    refetchQueries: [ListByIdDocument],
    awaitRefetchQueries: true
  });

  return useFormHandler<ListDetailsFormValues>(
    useCallback(
      async (data) => {
        if (data) {
          await listUpdateByIdMutation({
            variables: {
              id: list._id,
              record: {
                name: createMultilingual(data.name ?? ""),
                description: createMultilingual(data.description ?? ""),
              },
            },
          })
            .then((response) => (response.data ? onSuccess() : onError()))
            .catch(() => onError());
        }
      },
      [list, listUpdateByIdMutation, onError, onSuccess],
    ),
    defaultValues,
    { schema: listDetailsSchema, mode: "onBlur" },
  );
};
